import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatBRL, formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ScreenSpinner } from "@/components/ui/Spinner";

// Extrato do próprio usuário (GET /api/me/transactions) — porta da tela
// extrato.tsx do app original.
type TransactionType =
  | "deposit"
  | "withdraw"
  | "bet"
  | "prize"
  | "bonus"
  | "credit";

type TxRow = {
  id: string;
  type: TransactionType;
  amount: number;
  tournamentId: string | null;
  tournamentName: string | null;
  description: string | null;
  createdAt: string;
};

const TYPE_LABEL: Record<TransactionType, string> = {
  deposit: "Estorno",
  withdraw: "Saque",
  bet: "Palpite",
  prize: "Prêmio",
  bonus: "Ajuste",
  credit: "Crédito",
};

const TYPE_TONE: Record<
  TransactionType,
  "success" | "accent" | "muted" | "destructive"
> = {
  deposit: "success",
  withdraw: "destructive",
  bet: "muted",
  prize: "success",
  bonus: "accent",
  credit: "accent",
};

export default function ExtratoPage() {
  const [rows, setRows] = useState<TxRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ transactions: TxRow[] }>("/api/me/transactions")
      .then((data) => {
        if (!cancelled) setRows(data.transactions);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Erro ao carregar o extrato.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <div className="flex items-center gap-3">
        <Link
          to="/perfil"
          aria-label="Voltar"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </Link>
        <h1 className="text-xl font-bold">Extrato</h1>
      </div>

      {error && (
        <Card className="p-4 text-center text-sm text-[#ffb4ad]">{error}</Card>
      )}
      {!rows && !error && <ScreenSpinner />}

      <div className="flex flex-col gap-2 pb-6">
        {rows?.map((tx) => (
          <Card key={tx.id} className="flex items-center gap-3 p-3.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">
                {tx.description ?? TYPE_LABEL[tx.type]}
              </p>
              {tx.tournamentName && (
                <p className="truncate text-xs text-muted-foreground">
                  {tx.tournamentName}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                {formatDateTime(tx.createdAt)}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <p
                className={`text-sm font-bold ${
                  tx.amount >= 0 ? "text-tint" : "text-[#ffb4ad]"
                }`}
              >
                {tx.amount >= 0 ? "+" : ""}
                {formatBRL(tx.amount)}
              </p>
              <Chip tone={TYPE_TONE[tx.type]}>{TYPE_LABEL[tx.type]}</Chip>
            </div>
          </Card>
        ))}
        {rows && rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded-(--radius-app) border border-border bg-card px-6 py-12 text-center">
            <ScrollText size={32} className="text-muted-foreground" />
            <p className="text-sm font-semibold">Nenhuma movimentação ainda</p>
            <p className="text-xs text-muted-foreground">
              Créditos recebidos, palpites, prêmios e saques aparecem aqui.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
